import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadPdf, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Receipt, ChevronDown, ChevronRight, Download, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

const FILTERS = [
  { value: "pending",  label: "Awaiting invoice" },
  { value: "invoiced", label: "Invoiced — not yet paid" },
  { value: "paid",     label: "Paid" },
  { value: "void",     label: "Void" },
  { value: "all",      label: "All" },
];

// Map between the tracking row state and the tri-state dropdown.
function rowToPaidState(r) {
  if ((r.status || "") === "void") return "void";
  return r.paid ? "yes" : "no";
}

export default function SubcontractorInvoices() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("pending");
  const [supplier, setSupplier] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  // Per-row editable invoice metadata
  const [drafts, setDrafts] = useState({});
  // Per-PO upload count — used to block Save until at least one file is uploaded.
  const [uploadCounts, setUploadCounts] = useState({});

  const refreshUploads = async (id) => {
    try {
      const { data } = await api.get(`/uploads?entity_type=po&entity_id=${id}`);
      setUploadCounts(c => ({ ...c, [id]: (data || []).length }));
    } catch (_) {
      setUploadCounts(c => ({ ...c, [id]: 0 }));
    }
  };

  const load = () => api.get("/purchase-orders-register").then(r => setRows(r.data?.rows || [])).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = rows;
    const isVoid = (r) => (r.status || "") === "void";
    if (filter === "pending")  list = list.filter(r => !r.invoice_received && !isVoid(r));
    if (filter === "invoiced") list = list.filter(r => r.invoice_received && !r.paid && !isVoid(r));
    if (filter === "paid")     list = list.filter(r => r.paid && !isVoid(r));
    if (filter === "void")     list = list.filter(r => isVoid(r));
    if (supplier !== "all")    list = list.filter(r => (r.supplier || "—") === supplier);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(r => [r.po_number, r.supplier, r.invoice_number, r.reference, r.description].filter(Boolean).some(s => String(s).toLowerCase().includes(q)));
    return list;
  }, [rows, filter, supplier, search]);

  const suppliers = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.supplier) set.add(r.supplier); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const counts = useMemo(() => {
    const isVoid = (r) => (r.status || "") === "void";
    return {
      pending:  rows.filter(r => !r.invoice_received && !isVoid(r)).length,
      invoiced: rows.filter(r => r.invoice_received && !r.paid && !isVoid(r)).length,
      paid:     rows.filter(r => r.paid && !isVoid(r)).length,
      void:     rows.filter(r => isVoid(r)).length,
      total:    rows.length,
    };
  }, [rows]);

  const totalPending = useMemo(
    () => rows.filter(r => !r.invoice_received && (r.status || "") !== "void").reduce((s, r) => s + (Number(r.total) || 0), 0),
    [rows]
  );

  const toggle = (id, row) => {
    setExpandedId(curr => {
      if (curr === id) return null;
      setDrafts(d => ({ ...d, [id]: d[id] || {
        invoice_number: row.invoice_number || "",
        invoice_date:   row.invoice_date   || "",
        invoice_amount: row.invoice_amount ?? "",
        paid_state:     rowToPaidState(row),   // "no" | "yes" | "void"
      }}));
      // Fetch how many invoice files are currently attached to this PO
      refreshUploads(id);
      return id;
    });
  };

  const updateDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const save = async (id) => {
    const d = drafts[id] || {};
    const state = d.paid_state || "no";
    try {
      await api.patch(`/purchase-orders/${id}/tracking`, {
        invoice_number: d.invoice_number || "",
        invoice_date:   d.invoice_date   || "",
        invoice_amount: d.invoice_amount === "" || d.invoice_amount == null ? null : Number(d.invoice_amount),
        // Don't auto-flip invoice_received here — the file upload does that.
        // But if the user typed an invoice number without uploading yet, still mark received.
        ...(d.invoice_number ? { invoice_received: true } : {}),
        paid: state === "yes",
        // Drive the PO status explicitly so toggling out of "Void" clears it.
        status: state === "void" ? "void" : (state === "yes" ? "clear" : ""),
      });
      toast.success("Invoice details saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="subcontractor-invoices-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Accounts Payable</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Subcontractor invoices</h1>
        <p className="text-sm text-slate-600 mt-1">Every Purchase Order issued. Type the supplier&apos;s invoice number, date and amount, upload their invoice PDF — and the PO register updates automatically.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="invoice-tiles">
        <Tile label="Awaiting invoice"  value={counts.pending}  sub={`£${totalPending.toFixed(2)} outstanding`} tone="amber"
              active={filter === "pending"}  onClick={() => setFilter("pending")}  testid="tile-pending" />
        <Tile label="Invoiced · unpaid" value={counts.invoiced} tone="slate"
              active={filter === "invoiced"} onClick={() => setFilter("invoiced")} testid="tile-invoiced" />
        <Tile label="Paid"              value={counts.paid}     tone="emerald"
              active={filter === "paid"}     onClick={() => setFilter("paid")}     testid="tile-paid" />
        <Tile label="Void"              value={counts.void}     tone="rose"
              active={filter === "void"}     onClick={() => setFilter("void")}     testid="tile-void" />
        <Tile label="Total POs"         value={counts.total}    tone="slate"
              active={filter === "all"}      onClick={() => setFilter("all")}      testid="tile-total" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inv-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO no, supplier, invoice no…" className="pl-9 rounded-sm border-slate-300" />
        </div>
        <div className="min-w-[220px]">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="inv-filter" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>{FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger data-testid="inv-supplier-filter" className="rounded-sm border-slate-300"><SelectValue placeholder="All subcontractors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subcontractors</SelectItem>
              {suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 tabular-nums ml-auto">{filtered.length} of {rows.length}</div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th className="w-8"></Th>
                <Th>PO #</Th>
                <Th>Date</Th>
                <Th>Supplier</Th>
                <Th>PO total</Th>
                <Th>Invoice #</Th>
                <Th>Invoice date</Th>
                <Th>Status</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500 text-sm">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {rows.length === 0 ? "No purchase orders yet." : "No POs match your filter."}
                  </td>
                </tr>
              )}
              {filtered.map(r => {
                const isOpen = expandedId === r.id;
                const draft  = drafts[r.id] || {};
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-slate-200 hover:bg-slate-50">
                      <Td className="pr-0">
                        <button data-testid={`inv-expand-${r.id}`} onClick={() => toggle(r.id, r)} className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-600">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </Td>
                      <Td><div className="font-medium text-slate-900 tabular-nums">{r.po_number}</div></Td>
                      <Td><div className="tabular-nums text-slate-700">{r.date || "—"}</div></Td>
                      <Td><div className="text-slate-900">{r.supplier || "—"}</div></Td>
                      <Td><div className="tabular-nums text-slate-900">£{Number(r.total || 0).toFixed(2)}</div></Td>
                      <Td><div className="tabular-nums text-slate-700">{r.invoice_number || "—"}</div></Td>
                      <Td><div className="tabular-nums text-slate-700">{r.invoice_date || "—"}</div></Td>
                      <Td>
                        {(r.status || "") === "void" ? (
                          <Badge data-testid={`inv-status-void-${r.id}`} className="rounded-sm bg-rose-100 text-rose-800 hover:bg-rose-100 border border-rose-200">Void</Badge>
                        ) : r.paid ? (
                          <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Paid</Badge>
                        ) : r.invoice_received ? (
                          <Badge className="rounded-sm bg-slate-100 text-slate-800 hover:bg-slate-100 border border-slate-200">Invoiced</Badge>
                        ) : (
                          <Badge className="rounded-sm bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200">Awaiting invoice</Badge>
                        )}
                      </Td>
                      <Td className="text-right pr-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`inv-po-pdf-${r.id}`}
                          className="rounded-sm h-8 w-8 p-0"
                          title="Download PO PDF"
                          onClick={() => downloadPdf(`/purchase-orders/${r.id}/pdf`, `${r.number}.pdf`)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button asChild size="sm" variant="ghost" data-testid={`inv-po-open-${r.id}`} className="rounded-sm h-8 w-8 p-0" title="Open PO register">
                          <Link to="/purchase-orders/register">
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        </Button>
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/70 border-t border-slate-200">
                        <td colSpan={9} className="px-6 py-5 space-y-5">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                              <Label className="text-xs text-slate-600">Supplier invoice no.</Label>
                              <Input data-testid={`inv-num-${r.id}`} value={draft.invoice_number || ""} onChange={(e) => updateDraft(r.id, { invoice_number: e.target.value })} className="mt-1 rounded-sm border-slate-300" placeholder="INV-…" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">Invoice date</Label>
                              <Input data-testid={`inv-date-${r.id}`} type="date" value={draft.invoice_date || ""} onChange={(e) => updateDraft(r.id, { invoice_date: e.target.value })} className="mt-1 rounded-sm border-slate-300" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">Invoice amount (£)</Label>
                              <Input data-testid={`inv-amount-${r.id}`} type="number" step="0.01" value={draft.invoice_amount ?? ""} onChange={(e) => updateDraft(r.id, { invoice_amount: e.target.value })} className="mt-1 rounded-sm border-slate-300" placeholder={Number(r.total || 0).toFixed(2)} />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">Status</Label>
                              <Select value={draft.paid_state || "no"} onValueChange={(v) => updateDraft(r.id, { paid_state: v })}>
                                <SelectTrigger data-testid={`inv-paid-${r.id}`} className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="no">Not yet</SelectItem>
                                  <SelectItem value="yes">Paid</SelectItem>
                                  <SelectItem value="void">Void</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end">
                              {(() => {
                                const isVoid  = (draft.paid_state || "no") === "void";
                                const hasFile = (uploadCounts[r.id] || 0) > 0;
                                // Voiding a PO doesn't require an invoice PDF.
                                const canSave = isVoid || hasFile;
                                return (
                                  <Button onClick={() => save(r.id)} disabled={!canSave} data-testid={`inv-save-${r.id}`} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed" title={canSave ? "Save invoice details" : "Upload the invoice PDF first"}>
                                    <Save className="w-4 h-4 mr-1.5" /> Save
                                  </Button>
                                );
                              })()}
                            </div>
                          </div>

                          {(() => {
                            const isVoid  = (draft.paid_state || "no") === "void";
                            const hasFile = (uploadCounts[r.id] || 0) > 0;
                            return !isVoid && !hasFile && (
                              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid={`inv-warn-no-file-${r.id}`}>
                                You need to upload the supplier&apos;s invoice PDF below before you can save these details.
                              </div>
                            );
                          })()}

                          <FileUploader
                            entityType="po"
                            entityId={r.id}
                            label={`Upload supplier invoice — ${r.po_number} · ${r.supplier || ""}`}
                            onChange={(list) => {
                              setUploadCounts(c => ({ ...c, [r.id]: (list || []).length }));
                              load();
                            }}
                          />
                          <div className="text-xs text-slate-500">Tip: after uploading the invoice, click <span className="font-medium">Save</span> above (or refresh) to see the status flip to &quot;Invoiced&quot; — the PO register updates automatically.</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Tile({ label, value, sub, tone, active = false, onClick, testid }) {
  const tones = {
    amber:   { base: "border-amber-200 bg-amber-50",   ring: "ring-amber-400" },
    emerald: { base: "border-emerald-200 bg-emerald-50", ring: "ring-emerald-400" },
    rose:    { base: "border-rose-200 bg-rose-50",     ring: "ring-rose-400" },
    slate:   { base: "border-slate-200 bg-white",      ring: "ring-slate-400" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`text-left rounded-sm border ${t.base} p-4 transition
        hover:bg-opacity-80 hover:-translate-y-[1px] hover:shadow-sm cursor-pointer
        ${active ? `ring-2 ${t.ring} shadow-sm` : ""}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-display font-semibold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </button>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>; }
